/**
 * React Query hook for fetching user-specific feature flags from the backend.
 *
 * Uses /api/v1/features/me to get features based on the user's product access,
 * not just global feature flags.
 */

import { useQuery } from "@tanstack/react-query";
import { useApi, useApiReady } from "@/hooks/useApi";
import type { FeatureFlags, UserFeaturesResponse } from "@/types/features";

/**
 * Hook to fetch user-specific feature flags from the backend.
 *
 * This hook fetches features based on the user's product access:
 * - A feature is enabled if: global_flag_enabled AND user_has_product_with_feature
 * - If not authenticated, all features are disabled
 *
 * @returns Object with feature flags data, products list, loading state, and error state
 *
 * @example
 * ```tsx
 * const { data: features, isLoading, products } = useFeatureFlags();
 *
 * if (features?.demo_flow) {
 *   return <DemoFlowPage />;
 * }
 *
 * // Check product access
 * if (products?.includes("security")) {
 *   // User has security product
 * }
 * ```
 */
export function useFeatureFlags() {
  const api = useApi();
  const isApiReady = useApiReady();

  const query = useQuery<UserFeaturesResponse>({
    queryKey: ["feature-flags", "me"],
    queryFn: async () => {
      const data = await api.get<UserFeaturesResponse>("/api/v1/features/me");
      return data;
    },
    staleTime: 60 * 1000, // Cache for 1 minute (matches backend USER_FEATURE_CACHE_TTL)
    retry: 1,
    // Only fetch when API is ready (authenticated and token getter is set)
    enabled: isApiReady,
  });

  // Loading if API is not ready OR query is loading
  const isLoading = !isApiReady || query.isLoading;

  // Return a shape that's backward-compatible with existing usage
  // while also providing access to the full response
  return {
    ...query,
    // Override isLoading to include auth loading state
    isLoading,
    // Provide direct access to features for backward compatibility
    data: query.data?.features as FeatureFlags | undefined,
    // Also expose the full response for components that need products
    response: query.data,
    products: query.data?.products ?? [],
    isAuthenticated: query.data?.is_authenticated ?? false,
  };
}
